/**
 * Painel do agente CMO (E7.4) — última execução de cada job agendado, fila de
 * disparo manual e histórico do resumo semanal. Endpoints em
 * `docs/blog/api-contracts.md`, seção 7.
 *
 * **Os nomes aqui são os slugs que o notifyme realmente aceita** em
 * `POST v1/backoffice/blog/jobs/{name}/run` e no filtro `job_name` de
 * `GET .../jobs/runs` (`BlogJobNames` no backend) — e divergem de
 * `docs/blog/jobs.md`, que ainda descreve os nomes antigos em PascalCase.
 * Esta lista é a fonte da verdade, porque é o que o backend responde.
 */

export type JobName =
  | "search-console-sync"
  | "analytics-rollup"
  | "topic-generation"
  | "content-audit"
  | "weekly-digest"
  | "topic-evaluation"
  | "daily-post";

export const JOB_NAMES: JobName[] = [
  "search-console-sync",
  "analytics-rollup",
  "topic-generation",
  "topic-evaluation",
  "daily-post",
  "content-audit",
  "weekly-digest",
];

export const JOB_LABELS: Record<JobName, string> = {
  "search-console-sync": "Sincronização do Search Console",
  "analytics-rollup": "Consolidação do analytics",
  "topic-generation": "Geração de pautas",
  "content-audit": "Sugestões de atualização",
  "weekly-digest": "Resumo semanal por e-mail",
  "topic-evaluation": "Avaliação de pautas",
  "daily-post": "Post diário automático",
};

export const JOB_DESCRIPTIONS: Record<JobName, string> = {
  "search-console-sync":
    "Traz posição, cliques e impressões do Google Search Console para os posts do blog. Agendado diariamente às 6h.",
  "analytics-rollup":
    "Consolida os eventos de leitura do blog nos números que abastecem o Analytics. Agendamento definido no notifyme.",
  "topic-generation":
    "Gera pautas novas para a fila a partir dos clusters e do que está em falta. Agendamento definido no notifyme.",
  "content-audit":
    "Varre os posts publicados e gera sugestões de atualização na fila. Agendado no dia 1 de cada mês às 8h.",
  "weekly-digest":
    "Consolida o desempenho da semana e envia o resumo por e-mail. Agendado às sextas-feiras às 8h.",
  "topic-evaluation":
    "Dá nota e veredito às pautas sugeridas e aponta conflito com posts já publicados. Agendado diariamente às 6h30.",
  "daily-post":
    "Gera o post da melhor pauta aprovada e agenda para as 7h do dia seguinte, com e-mail de prévia e botão CANCELAR. Agendado diariamente às 12h.",
};

/** Espelha `BlogJobRunStatus` do notifyme. */
export type JobRunStatus = "running" | "succeeded" | "failed";
/** Espelha `BlogJobRunTrigger` do notifyme. */
export type JobRunTrigger = "scheduled" | "manual";

/** Uma execução registrada em `blog.job_runs` (`ResponseBlogJobRun`). */
export interface BlogJobRun {
  id: string;
  jobName: JobName;
  status: JobRunStatus;
  startedAt: string;
  finishedAt: string | null;
  trigger: JobRunTrigger;
  /** Resumo em texto que o job devolve ao terminar (ex.: pautas criadas). */
  summary: string | null;
  error: string | null;
  itemsProcessed: number;
}

/** Um resumo semanal já enviado (histórico do `weekly-digest`). */
export interface WeeklyDigestSummary {
  id: string;
  weekStartDate: string;
  weekEndDate: string;
  highlightsMarkdown: string;
  sentAt: string;
}

/** Operações do painel do CMO sobre jobs e resumos semanais. */
export interface JobsService {
  /** Última execução de cada job (um item por `jobName`, ou nenhum se nunca rodou). */
  listLatestRuns(): Promise<BlogJobRun[]>;
  /** Disparo manual, restrito a admin no backend real. Resolve com o id da execução. */
  runJobNow(jobName: JobName): Promise<{ runId: string }>;
  /** Histórico dos resumos semanais já enviados, mais recente primeiro. */
  listWeeklyDigests(): Promise<WeeklyDigestSummary[]>;
}
